import { z } from 'zod';
import { paginacaoSchema } from './paginacao';
import { pagamentosVendaSchema, type MeioPagamento } from './pagamentos';

export const STATUS_VEICULO = ['preparacao', 'disponivel', 'reservado', 'vendido'] as const;
export const ETAPAS_PREPARACAO = [
  'mecanica',
  'funilaria',
  'polimento',
  'documentacao',
  'vistoria',
  'fotos',
] as const;
export const TIPOS_ENTRADA = ['compra_pf', 'compra_loja', 'troca', 'consignacao'] as const;
export const CATEGORIAS_CUSTO = [
  'mecanica',
  'funilaria',
  'polimento',
  'documentacao',
  'vistoria',
  'outros',
] as const;
export const FORMAS_PAGAMENTO = ['a_vista', 'financiado', 'misto'] as const;
export const TIPOS_CONSULTA = ['debitos', 'laudo_cautelar', 'vistoria'] as const;

export type StatusVeiculo = (typeof STATUS_VEICULO)[number];
export type EtapaPreparacao = (typeof ETAPAS_PREPARACAO)[number];
export type TipoEntrada = (typeof TIPOS_ENTRADA)[number];
export type CategoriaCusto = (typeof CATEGORIAS_CUSTO)[number];
export type FormaPagamento = (typeof FORMAS_PAGAMENTO)[number];
export type TipoConsulta = (typeof TIPOS_CONSULTA)[number];

/** Rótulos no vocabulário do lojista. */
export const ROTULO_STATUS: Record<StatusVeiculo, string> = {
  preparacao: 'Em preparação',
  disponivel: 'À venda',
  reservado: 'Reservado',
  vendido: 'Vendido',
};
export const ROTULO_ETAPA: Record<EtapaPreparacao, string> = {
  mecanica: 'Mecânica',
  funilaria: 'Funilaria',
  polimento: 'Polimento',
  documentacao: 'Documentação',
  vistoria: 'Vistoria',
  fotos: 'Fotos',
};
export const ROTULO_ENTRADA: Record<TipoEntrada, string> = {
  compra_pf: 'Compra de particular',
  compra_loja: 'Compra de outra loja',
  troca: 'Troca na negociação',
  consignacao: 'Consignação',
};
export const ROTULO_CATEGORIA: Record<CategoriaCusto, string> = {
  mecanica: 'Mecânica',
  funilaria: 'Funilaria',
  polimento: 'Polimento',
  documentacao: 'Documentação',
  vistoria: 'Vistoria',
  outros: 'Outros',
};
export const ROTULO_FORMA: Record<FormaPagamento, string> = {
  a_vista: 'À vista',
  financiado: 'Financiado',
  misto: 'Entrada + financiamento',
};

/** Placa antiga (ABC1234) ou Mercosul (ABC1D23). Normaliza para maiúsculas sem traço. */
export const normalizarPlaca = (p: string): string => p.replace(/[\s-]/g, '').toUpperCase();
export const placaSchema = z
  .string()
  .transform(normalizarPlaca)
  .refine((p) => /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(p), 'Placa inválida');

const anoAtual = () => new Date().getFullYear();
const centavos = z.number().int().min(0).max(100_000_000_00);
const textoOpc = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v ? v : null));

const baseVeiculo = z.object({
  placa: placaSchema,
  chassi: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-HJ-NPR-Z0-9]{17}$/, 'Chassi deve ter 17 caracteres')
    .optional()
    .nullable()
    .or(z.literal('').transform(() => null)),
  renavam: z
    .string()
    .trim()
    .regex(/^\d{9,11}$/, 'RENAVAM deve ter 9 a 11 números')
    .optional()
    .nullable()
    .or(z.literal('').transform(() => null)),
  marca: z.string().trim().min(2).max(60),
  modelo: z.string().trim().min(1).max(80),
  versao: textoOpc(120),
  anoFabricacao: z.number().int().min(1950),
  anoModelo: z.number().int().min(1950),
  km: z.number().int().min(0).max(2_000_000),
  cor: textoOpc(40),
  combustivel: textoOpc(40),
  cambio: textoOpc(40),
  // Sem padrão aqui: na edição (partial) um padrão apagaria os opcionais não enviados
  opcionais: z.array(z.string().trim().min(1).max(60)).max(60).optional(),
  observacoes: textoOpc(2000),
  tipoEntrada: z.enum(TIPOS_ENTRADA),
  valorCompraCentavos: centavos,
  precoCentavos: centavos.optional().nullable(),
  fipeCodigo: textoOpc(20),
  fipeCentavos: centavos.optional().nullable(),
});

const anosCoerentes = (v: { anoFabricacao?: number; anoModelo?: number }) =>
  v.anoFabricacao === undefined ||
  v.anoModelo === undefined ||
  (v.anoModelo >= v.anoFabricacao &&
    v.anoModelo <= v.anoFabricacao + 1 &&
    v.anoFabricacao <= anoAtual() + 1);

export const veiculoCriarSchema = baseVeiculo
  .extend({
    opcionais: z.array(z.string().trim().min(1).max(60)).max(60).default([]),
    filialId: z.string().uuid().optional(),
    status: z.enum(['preparacao', 'disponivel']).default('preparacao'),
    etapa: z.enum(ETAPAS_PREPARACAO).optional().nullable(),
    entradaEm: z.coerce.date().optional(),
  })
  .refine(anosCoerentes, {
    message: 'Ano modelo deve ser igual ou 1 a mais que o de fabricação',
    path: ['anoModelo'],
  })
  .refine((v) => v.status !== 'disponivel' || (v.precoCentavos ?? 0) > 0, {
    message: 'Informe o preço para colocar à venda',
    path: ['precoCentavos'],
  });
export type VeiculoCriarInput = z.input<typeof veiculoCriarSchema>;
export type VeiculoCriar = z.output<typeof veiculoCriarSchema>;

export const veiculoAtualizarSchema = baseVeiculo
  .omit({ precoCentavos: true })
  .partial()
  .refine(anosCoerentes, {
    message: 'Ano modelo deve ser igual ou 1 a mais que o de fabricação',
    path: ['anoModelo'],
  });
export type VeiculoAtualizar = z.output<typeof veiculoAtualizarSchema>;

export const mudarStatusSchema = z
  .object({
    status: z.enum(['preparacao', 'disponivel', 'reservado']),
    etapa: z.enum(ETAPAS_PREPARACAO).optional().nullable(),
  })
  .refine((v) => v.status !== 'preparacao' || v.etapa, {
    message: 'Informe a etapa da preparação',
    path: ['etapa'],
  });
export const mudarPrecoSchema = z.object({
  precoCentavos: centavos.refine((v) => v > 0, 'Preço deve ser maior que zero'),
});
export const custoCriarSchema = z.object({
  categoria: z.enum(CATEGORIAS_CUSTO),
  descricao: z.string().trim().min(2).max(200),
  valorCentavos: centavos.refine((v) => v > 0, 'Valor deve ser maior que zero'),
  data: z.coerce.date().optional(),
  fornecedor: textoOpc(120),
});
export const vendaCriarSchema = z.object({
  valorCentavos: centavos.refine((v) => v > 0, 'Valor deve ser maior que zero'),
  forma: z.enum(FORMAS_PAGAMENTO),
  data: z.coerce.date().optional(),
  // Fase 3: quem vendeu, para quem, financiamento, extras e troca
  vendedorId: z.string().uuid().optional(),
  clienteId: z.string().uuid().optional(),
  leadId: z.string().uuid().optional(),
  propostaId: z.string().uuid().optional(),
  trocaId: z.string().uuid().optional(),
  financiadoCentavos: centavos.nullable().optional(),
  bancoFinanciamento: textoOpc(80),
  /** Fase 4: proposta de financiamento aprovada usada nesta venda */
  propostaFinanciamentoId: z.string().uuid().optional(),
  /**
   * Como o cliente pagou (Pix, dinheiro, cartão, boleto, cheque, crediário, financiamento).
   * Soma = valor + acessórios − troca − sinal. Sem a lista, vale o modo antigo (à vista/financiado).
   */
  pagamentos: pagamentosVendaSchema.optional(),
  /** evento da loja em que a venda aconteceu (sem informar, o sistema liga sozinho) */
  eventoId: z.string().uuid().optional(),
  adicionais: z
    .array(
      z.object({
        tipo: z.enum(['acessorio', 'garantia', 'seguro']),
        descricao: z.string().trim().min(2).max(120),
        valorCentavos: centavos.refine((v) => v > 0),
        parceiro: textoOpc(60),
        comissaoCentavos: centavos.optional(),
      }),
    )
    .max(20)
    .default([]),
});
export const transferirSchema = z.object({ filialId: z.string().uuid() });
export const consultaCriarSchema = z.object({ tipo: z.enum(TIPOS_CONSULTA) });

export const filtroEstoqueSchema = z.object({
  status: z.enum([...STATUS_VEICULO, 'estoque']).optional(),
  busca: z.string().trim().max(60).optional(),
  faixa: z.enum(['ate30', '31a60', '61a90', 'mais90', 'mais60']).optional(),
  marca: z.string().trim().max(40).optional(),
  ordem: z.enum(['recentes', 'dias', 'preco']).default('recentes'),
  ...paginacaoSchema.shape,
});

/** Números do topo da tela Estoque. Custo só para quem pode ver custos. */
export interface ResumoEstoque {
  contagens: {
    estoque: number;
    disponivel: number;
    preparacao: number;
    reservado: number;
    vendidoMes: number;
    parados60: number;
  };
  valorAnunciadoCentavos: number;
  custoEstoqueCentavos: number | null;
  diasMedios: number;
  semPreco: number;
  marcas: string[];
}
export type FiltroEstoque = z.infer<typeof filtroEstoqueSchema>;

/** Alerta de tempo em estoque (docs/03): 30, 60 e 90 dias. */
export function alertaDias(dias: number): 0 | 30 | 60 | 90 {
  if (dias >= 90) return 90;
  if (dias >= 60) return 60;
  if (dias >= 30) return 30;
  return 0;
}

export function faixaDias(dias: number): 'ate30' | '31a60' | '61a90' | 'mais90' {
  if (dias <= 30) return 'ate30';
  if (dias <= 60) return '31a60';
  if (dias <= 90) return '61a90';
  return 'mais90';
}

/** Custos: só para quem pode ver (dono, gerente, financeiro). Vendedor recebe `null`. */
export interface CustosVeiculo {
  valorCompraCentavos: number;
  custoPreparacaoCentavos: number;
  custoTotalCentavos: number;
  margemPrevistaCentavos: number | null;
  margemPrevistaPercentual: number | null;
  lucroRealCentavos: number | null;
}

export interface VeiculoResumo {
  id: string;
  filialId: string;
  placa: string;
  marca: string;
  modelo: string;
  versao: string | null;
  anoFabricacao: number;
  anoModelo: number;
  km: number;
  cor: string | null;
  status: StatusVeiculo;
  etapa: EtapaPreparacao | null;
  precoCentavos: number | null;
  fipeCentavos: number | null;
  diasEstoque: number;
  alerta: 0 | 30 | 60 | 90;
  fotoUrl: string | null;
  custos: CustosVeiculo | null;
}

export interface VeiculoDetalhe extends VeiculoResumo {
  chassi: string | null;
  renavam: string | null;
  combustivel: string | null;
  cambio: string | null;
  opcionais: string[];
  observacoes: string | null;
  tipoEntrada: TipoEntrada;
  fipeCodigo: string | null;
  fipeConsultadaEm: string | null;
  entradaEm: string;
  vendidoEm: string | null;
  fotos: {
    id: string;
    url: string;
    urlGrande: string;
    processada: boolean;
    /** Tratada pela Mídia com IA */
    tratadaIa?: boolean;
  }[];
  listaCustos:
    | {
        id: string;
        categoria: CategoriaCusto;
        descricao: string;
        valorCentavos: number;
        data: string;
        fornecedor: string | null;
      }[]
    | null;
  venda: {
    valorCentavos: number;
    forma: FormaPagamento;
    data: string;
    lucroCentavos: number | null;
    /** como o cliente pagou (vazio em vendas antigas) */
    pagamentos: { meio: MeioPagamento; valorCentavos: number; parcelas: number }[];
  } | null;
  consultas: {
    id: string;
    tipo: TipoConsulta;
    status: string;
    provedor: string;
    resultado: unknown;
    criadoEm: string;
  }[];
}

/** Calcula custo e margem (fonte única usada pela API e pelos testes). */
export function calcularCustos(v: {
  valorCompraCentavos: number;
  custoPreparacaoCentavos: number;
  precoCentavos: number | null;
  vendaCentavos?: number | null;
}): CustosVeiculo {
  const custoTotal = v.valorCompraCentavos + v.custoPreparacaoCentavos;
  const margem = v.precoCentavos ? v.precoCentavos - custoTotal : null;
  return {
    valorCompraCentavos: v.valorCompraCentavos,
    custoPreparacaoCentavos: v.custoPreparacaoCentavos,
    custoTotalCentavos: custoTotal,
    margemPrevistaCentavos: margem,
    margemPrevistaPercentual:
      margem !== null && v.precoCentavos
        ? Math.round((margem / v.precoCentavos) * 1000) / 10
        : null,
    lucroRealCentavos: v.vendaCentavos ? v.vendaCentavos - custoTotal : null,
  };
}

/** Converte "R$ 45.900,50" / "45900.5" / 45900 em centavos. */
export function reaisParaCentavos(v: string | number): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? Math.round(v * 100) : null;
  const s = v.replace(/[R$\s]/g, '');
  if (!s) return null;
  const normal = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s;
  const n = Number(normal);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

// ─────────── Avaliação de entrada ───────────

export const ESTADOS_ITEM_AVALIACAO = ['ok', 'atencao', 'ruim'] as const;
export type EstadoItemAvaliacao = (typeof ESTADOS_ITEM_AVALIACAO)[number];

/** Checklist padrão da avaliação de entrada, por parte do carro. */
export const CHECKLIST_AVALIACAO: { parte: string; itens: string[] }[] = [
  {
    parte: 'Lataria e pintura',
    itens: [
      'Para-choque dianteiro',
      'Capô',
      'Para-lamas',
      'Portas',
      'Teto',
      'Tampa traseira',
      'Para-choque traseiro',
      'Pintura (riscos, retoques)',
    ],
  },
  {
    parte: 'Vidros, faróis e lanternas',
    itens: ['Para-brisa', 'Vidros laterais e traseiro', 'Faróis', 'Lanternas', 'Retrovisores'],
  },
  { parte: 'Pneus e rodas', itens: ['Pneus dianteiros', 'Pneus traseiros', 'Estepe', 'Rodas'] },
  {
    parte: 'Interior',
    itens: [
      'Bancos',
      'Painel e comandos',
      'Ar-condicionado',
      'Som e multimídia',
      'Forro e tapetes',
    ],
  },
  {
    parte: 'Motor e câmbio',
    itens: ['Motor (ruídos, vazamentos)', 'Câmbio e embreagem', 'Arrefecimento', 'Bateria'],
  },
  { parte: 'Suspensão e freios', itens: ['Suspensão', 'Freios', 'Direção'] },
  {
    parte: 'Elétrica',
    itens: ['Luzes de alerta no painel', 'Vidros e travas elétricas', 'Partida'],
  },
  {
    parte: 'Documentos e acessórios',
    itens: [
      'Documento de transferência (CRV/ATPV)',
      'Multas e débitos',
      'Chave reserva',
      'Manual do proprietário',
      'Macaco e triângulo',
    ],
  },
];
/** Parte cujos itens com problema viram pendências do carro na entrada. */
export const PARTE_DOCUMENTOS = 'Documentos e acessórios';

export const itemAvaliacaoSchema = z.object({
  parte: z.string().trim().min(1).max(60),
  item: z.string().trim().min(1).max(80),
  estado: z.enum(ESTADOS_ITEM_AVALIACAO),
  observacao: textoOpc(300),
  custoCentavos: centavos.default(0),
});
export type ItemAvaliacao = z.output<typeof itemAvaliacaoSchema>;

export const avaliacaoSalvarSchema = z
  .object({
    filialId: z.string().uuid().optional(),
    origem: z.enum(TIPOS_ENTRADA),
    clienteId: z.string().uuid().optional().nullable(),
    proprietarioNome: textoOpc(120),
    proprietarioTelefone: textoOpc(30),
    placa: placaSchema,
    marca: z.string().trim().min(2).max(60),
    modelo: z.string().trim().min(1).max(80),
    versao: textoOpc(120),
    anoFabricacao: z.number().int().min(1950),
    anoModelo: z.number().int().min(1950),
    km: z.number().int().min(0).max(2_000_000),
    cor: textoOpc(40),
    combustivel: textoOpc(40),
    cambio: textoOpc(40),
    itens: z.array(itemAvaliacaoSchema).max(120).default([]),
    fipeCentavos: centavos.optional().nullable(),
    fipeCodigo: textoOpc(20),
    precoVendaPrevistoCentavos: centavos.optional().nullable(),
    margemPercentual: z.number().int().min(0).max(60).default(12),
    valorPedidoCentavos: centavos.optional().nullable(),
    valorOfertaCentavos: centavos.optional().nullable(),
    observacoes: textoOpc(2000),
  })
  .refine(anosCoerentes, {
    message: 'Ano modelo deve ser igual ou 1 a mais que o de fabricação',
    path: ['anoModelo'],
  });
export type AvaliacaoSalvarInput = z.input<typeof avaliacaoSalvarSchema>;
export type AvaliacaoSalvar = z.output<typeof avaliacaoSalvarSchema>;

export const avaliacaoAceitarSchema = z.object({
  valorCompraCentavos: centavos.refine((v) => v > 0, 'Informe quanto vai pagar'),
  precoCentavos: centavos.optional().nullable(),
});
export const avaliacaoRecusarSchema = z.object({ motivo: textoOpc(300) });

export interface CalculoAvaliacao {
  reparoPrevistoCentavos: number;
  itensComProblema: number;
  /** Preço de venda previsto ou, sem ele, a FIPE */
  precoBaseCentavos: number | null;
  margemCentavos: number | null;
  /** Quanto pagar no máximo para vender pelo preço previsto, com o reparo e a margem desejada */
  compraSugeridaCentavos: number | null;
}

export function calcularAvaliacao(a: {
  itens: Pick<ItemAvaliacao, 'estado' | 'custoCentavos'>[];
  precoVendaPrevistoCentavos?: number | null;
  fipeCentavos?: number | null;
  margemPercentual: number;
}): CalculoAvaliacao {
  const reparo = a.itens.reduce((s, i) => s + (i.estado === 'ok' ? 0 : i.custoCentavos), 0);
  const base = a.precoVendaPrevistoCentavos ?? a.fipeCentavos ?? null;
  const margem = base === null ? null : Math.round((base * a.margemPercentual) / 100);
  return {
    reparoPrevistoCentavos: reparo,
    itensComProblema: a.itens.filter((i) => i.estado !== 'ok').length,
    precoBaseCentavos: base,
    margemCentavos: margem,
    compraSugeridaCentavos:
      base === null ? null : Math.max(0, Math.floor((base - reparo - (margem ?? 0)) / 100) * 100),
  };
}

// ─────────── Pendências do carro e despachante ───────────

export const TIPOS_PENDENCIA = [
  'documento',
  'multas',
  'ipva',
  'licenciamento',
  'chave_reserva',
  'manual',
  'estepe',
  'vistoria',
  'outro',
] as const;
export type TipoPendencia = (typeof TIPOS_PENDENCIA)[number];
export const NOME_PENDENCIA: Record<TipoPendencia, string> = {
  documento: 'Documento de transferência (CRV/ATPV)',
  multas: 'Multas e débitos quitados',
  ipva: 'IPVA em dia',
  licenciamento: 'Licenciamento em dia',
  chave_reserva: 'Chave reserva',
  manual: 'Manual do proprietário',
  estepe: 'Estepe, macaco e triângulo',
  vistoria: 'Vistoria',
  outro: 'Outro',
};
/** Lista que o botão "Adicionar as de sempre" cria no carro. */
export const PENDENCIAS_PADRAO: TipoPendencia[] = [
  'documento',
  'multas',
  'ipva',
  'chave_reserva',
  'manual',
  'estepe',
];

export const pendenciaCriarSchema = z.object({
  tipo: z.enum(TIPOS_PENDENCIA),
  descricao: textoOpc(200),
  responsavelId: z.string().uuid().optional().nullable(),
  prazo: z.coerce.date().optional().nullable(),
});
export const pendenciaAtualizarSchema = z.object({
  resolvida: z.boolean().optional(),
  responsavelId: z.string().uuid().optional().nullable(),
  prazo: z.coerce.date().optional().nullable(),
});

export const TIPOS_PROCESSO_DESPACHANTE = [
  'transferencia_entrada',
  'transferencia_saida',
  'licenciamento',
  'segunda_via',
  'baixa_gravame',
  'outro',
] as const;
export type TipoProcessoDespachante = (typeof TIPOS_PROCESSO_DESPACHANTE)[number];
export const NOME_PROCESSO_DESPACHANTE: Record<TipoProcessoDespachante, string> = {
  transferencia_entrada: 'Transferência para a loja',
  transferencia_saida: 'Transferência para o comprador',
  licenciamento: 'Licenciamento',
  segunda_via: 'Segunda via de documento',
  baixa_gravame: 'Baixa de gravame (financiamento)',
  outro: 'Outro',
};
export const STATUS_PROCESSO_DESPACHANTE = ['aberto', 'enviado', 'concluido', 'cancelado'] as const;
export type StatusProcessoDespachante = (typeof STATUS_PROCESSO_DESPACHANTE)[number];

export const processoCriarSchema = z.object({
  tipo: z.enum(TIPOS_PROCESSO_DESPACHANTE),
  despachante: textoOpc(120),
  enviadoEm: z.coerce.date().optional().nullable(),
  previsaoEm: z.coerce.date().optional().nullable(),
  custoCentavos: centavos.default(0),
  observacoes: textoOpc(500),
});
// Edição: nada de padrão (um campo não enviado não pode virar zero)
export const processoAtualizarSchema = processoCriarSchema
  .extend({ custoCentavos: centavos.optional() })
  .partial()
  .extend({
    status: z.enum(STATUS_PROCESSO_DESPACHANTE).optional(),
    concluidoEm: z.coerce.date().optional().nullable(),
  });
